'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { recommend, QUESTIONS, type QuizAnswers, type ScorableSet } from '@sifly/quiz-engine'
import type { ProductFinderSet } from './ContentSections'
import { isDarkSurfaceTone, type SectionStyleOverrides } from '@/lib/styleOverrides'
import { SectionHeader } from './SectionHeader'
import { REC_QUERY, shouldShowRentalBridge } from './productFinderPayoff'
import { productFinderTone } from './productFinderTone'
import { privacyAllows } from '@/lib/privacyRuntime'

type Data = {
  heading?: string
  intro?: string
  style?: SectionStyleOverrides
}

type Props = {
  data: Data
  sets: ProductFinderSet[]
  tenantSlug: string | null
  /**
   * When the dealer offers rentals, the standalone /quiz page passes the
   * resolved rentals path so a first-time rider (confidence "unsure" = "new to
   * this") gets a "try before you buy" bridge on the results screen. Null (the
   * default, and what the in-page product_finder section passes) hides it.
   */
  rentalsHref?: string | null
  /**
   * When the tenant shows >=1 visible eFoil set, the standalone /quiz page
   * passes the resolved /build path (with ?from=quiz so Task 8's build_view
   * records where the visit came from) so the results screen offers "build
   * it yourself" as an alternative to the hero recommendation. Null (the
   * default, and what the in-page product_finder section passes) hides it —
   * same asymmetry as rentalsHref: the embedded section doesn't compute
   * either resolved link today.
   */
  buildHref?: string | null
  /** Authoritative dark-band flag from the dispatcher (accounts for
   *  brand-shaded surfaces); falls back to the local style tone. */
  onDark?: boolean
  /**
   * 'page' — the standalone /quiz page's full-width payoff: large hero image,
   * card row for the runner-up / build / rental paths. 'compact' (default,
   * and what the in-page product_finder section passes) keeps the embedded
   * 480px card. Question steps render identically in both — a narrow column
   * is right for answering; only the RESULT earns the width.
   */
  layout?: 'compact' | 'page'
}

// Headings follow the dealer's heading font + optional weight override, matching
// the other storefront section renderers (see CtaBandSections.tsx).
const HEADING_STYLE = { fontFamily: 'var(--font-heading)', fontWeight: 'var(--brand-font-heading-weight, 600)' }
const BUTTON_RADIUS = 'rounded-[var(--layout-button-radius,var(--brand-radius,0.5rem))]'
const IMAGE_RADIUS = 'rounded-[var(--layout-image-radius,var(--brand-radius,0.5rem))]'

// Fire-and-forget analytics to the same route the embed uses, only when the
// visitor's privacy choice allows analytics. Same-origin on the storefront, so
// a relative URL is fine. Tagged source:'onsite' so Quiz
// Insights can segment embed vs on-site later.
function track(
  tenantSlug: string | null,
  type: 'quiz_complete' | 'quiz_click',
  product: string,
  meta: Record<string, string>,
) {
  if (!tenantSlug || typeof window === 'undefined') return
  // On-site quiz events are site analytics: same opt-out, GPC and consent
  // gate as every other event (the route re-checks; this skips the request).
  if (!privacyAllows('analytics')) return
  const url = new URL('/api/embed/event', window.location.origin)
  url.searchParams.set('tenant', tenantSlug)
  url.searchParams.set('type', type)
  url.searchParams.set('embed', 'quiz')
  if (product) url.searchParams.set('product', product)
  url.searchParams.set('meta', JSON.stringify({ ...meta, source: 'onsite' }))
  if (navigator.sendBeacon) navigator.sendBeacon(url.toString())
  else fetch(url.toString(), { keepalive: true }).catch(() => {})
}

/**
 * Pins the model the quiz just recommended onto `buildHref`, so clicking
 * "Build a Board" opens the configurator on the SAME set the hero card above
 * it points at (`/products/<hero.slug>?rec=quiz`) — not whichever set
 * `/build` would default to on its own (its `sort_order`-first model).
 * Splits on the FIRST `?` only (via `indexOf`, not `String.split`, which has
 * no occurrence limit and would silently drop everything after a second `?`)
 * and rebuilds via `URLSearchParams` rather than string-concatenating
 * `&set=...` onto `buildHref`, so this stays correct whether or not
 * `buildHref` already carries a query (today it always does, `?from=quiz`,
 * but this must not assume that).
 */
function withRecommendedSet(buildHref: string, slug: string): string {
  const qIndex = buildHref.indexOf('?')
  const path = qIndex === -1 ? buildHref : buildHref.slice(0, qIndex)
  const query = qIndex === -1 ? '' : buildHref.slice(qIndex + 1)
  const params = new URLSearchParams(query)
  params.set('set', slug)
  return `${path}?${params.toString()}`
}

/** The "Build a Board" CTA shared by every results screen (matched, no-hero,
 *  no-recommendation) — same copy and placement everywhere; only the href
 *  (pinned to a hero slug, or left un-pinned when there is none) differs. */
function BuildLinkPrompt({ href, tone }: { href: string; tone: ReturnType<typeof productFinderTone> }) {
  return (
    <p className="mt-4 text-[13px]" style={{ color: tone.muted }}>
      Want full control over the spec?{' '}
      <Link href={href} style={{ color: tone.link }}>
        Build a Board →
      </Link>
    </p>
  )
}

function toScorable(sets: ProductFinderSet[]): ScorableSet[] {
  return sets
    .filter((s) => s.quiz)
    .map((s) => ({
      slug: s.slug,
      family: s.quiz!.family,
      volume: s.quiz!.volume,
      specialOrder: s.quiz!.specialOrder,
    }))
}

// Stable per-view session id so quiz_complete + quiz_click correlate.
function makeSession(): string {
  return `q_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e6).toString(36)}`
}

export function ProductFinderSection({ data, sets, tenantSlug, rentalsHref = null, buildHref = null, onDark: onDarkProp, layout = 'compact' }: Props) {
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<Partial<QuizAnswers>>({})
  const [session] = useState(makeSession)
  const [done, setDone] = useState(false)

  const scorable = toScorable(sets)
  // Casing-only mismatch vs the product_finder registry default ('Find your
  // board', packages/agent-foundation/src/registry/definitions.ts) —
  // deliberate, not a bug. Renaming the registry default costs a
  // FOUNDATION_VERSION bump + a 24 MB codegen diff for no user-visible gain.
  const heading = data.heading ?? 'Find Your Board'
  const onDark = onDarkProp ?? isDarkSurfaceTone(data.style)
  const tone = productFinderTone(onDark)

  function choose(key: keyof QuizAnswers, value: string) {
    const next = { ...answers, [key]: value }
    setAnswers(next)
    if (step + 1 >= QUESTIONS.length) setDone(true)
    else setStep(step + 1)
  }

  function restart() {
    setAnswers({})
    setStep(0)
    setDone(false)
  }

  if (done) {
    const rec = recommend(answers as QuizAnswers, scorable)
    return (
      <ProductFinderResult
        rec={rec}
        sets={sets}
        answers={answers as QuizAnswers}
        session={session}
        tenantSlug={tenantSlug}
        rentalsHref={rentalsHref}
        buildHref={buildHref}
        onDark={onDark}
        onRestart={restart}
        wide={layout === 'page'}
      />
    )
  }

  const q = QUESTIONS[step]
  const pct = Math.round((step / QUESTIONS.length) * 100)
  return (
    <section className="mx-auto w-full max-w-[var(--section-max-width,480px)] px-[var(--section-padding-x,1rem)] py-10">
      <SectionHeader data={{ heading, intro: data.intro }} onDark={onDark} align="start" />
      <div className={`mt-4 h-1 w-full overflow-hidden rounded-full ${tone.track}`}>
        <span
          className={`block h-full ${tone.fill} transition-[width] duration-200`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="mt-3 text-xs" style={{ color: tone.muted }}>
        Question {step + 1} of {QUESTIONS.length}
      </p>
      <h3 className="mt-1 text-base font-medium" style={{ ...HEADING_STYLE, color: tone.heading }}>
        {q.title}
      </h3>
      <div className="mt-2 space-y-2">
        {q.options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => choose(q.key, o.value)}
            className={`block w-full ${BUTTON_RADIUS} border px-4 py-3 text-left text-sm transition-colors ${tone.option}`}
          >
            {o.label}
          </button>
        ))}
      </div>
      {step > 0 ? (
        <button
          type="button"
          onClick={() => setStep(step - 1)}
          className={`mt-4 text-[13px] ${tone.back}`}
        >
          ← Back
        </button>
      ) : null}
    </section>
  )
}

function ProductFinderResult({
  rec,
  sets,
  answers,
  session,
  tenantSlug,
  rentalsHref,
  buildHref,
  onDark,
  onRestart,
  wide,
}: {
  rec: ReturnType<typeof recommend>
  sets: ProductFinderSet[]
  answers: QuizAnswers
  session: string
  tenantSlug: string | null
  rentalsHref: string | null
  buildHref: string | null
  onDark: boolean
  onRestart: () => void
  wide: boolean
}) {
  const tone = productFinderTone(onDark)
  const hero = rec ? sets.find((s) => s.slug === rec.heroSlug) : undefined
  const runner = rec?.runnerUpSlug ? sets.find((s) => s.slug === rec.runnerUpSlug) : null
  const lite = sets.find((s) => s.quiz?.specialOrder)

  // Fire quiz_complete exactly once per result view. In an effect (not the
  // render body) so StrictMode double-renders and discarded concurrent renders
  // don't over-count the rollups. Restart unmounts this component, so a second
  // completion remounts and correctly re-fires.
  useEffect(() => {
    if (rec && hero) {
      track(tenantSlug, 'quiz_complete', rec.heroSlug, {
        ...(answers as Record<string, string>),
        recommended: rec.heroSlug,
        runnerUp: rec.runnerUpSlug ?? '',
        session,
      })
    }
    // Fire once on mount; rec/answers are fixed for a given completion.
  }, [])

  // Fall-graceful: no recommendation (empty/lite-only catalog) → explore CTA.
  if (!rec) {
    return (
      <section className="mx-auto w-full max-w-[var(--section-max-width,480px)] px-[var(--section-padding-x,1rem)] py-10 [text-align:var(--section-text-align,center)]">
        <h3 className="text-base font-medium" style={{ ...HEADING_STYLE, color: tone.heading }}>
          Let&rsquo;s find your board
        </h3>
        <p className="mt-1 text-sm" style={{ color: tone.muted }}>
          Explore the full range to find your perfect match.
        </p>
        <Link href="/shop" className="mt-3 inline-block font-medium" style={{ color: tone.link }}>
          Explore the range →
        </Link>
        {/* No hero to pin to — degrade to the un-pinned link rather than
            emitting set=undefined. */}
        {buildHref ? <BuildLinkPrompt href={buildHref} tone={tone} /> : null}
      </section>
    )
  }

  if (!hero) {
    return (
      <section className="mx-auto w-full max-w-[var(--section-max-width,480px)] px-[var(--section-padding-x,1rem)] py-10 [text-align:var(--section-text-align,center)]">
        <p className="text-sm" style={{ color: tone.muted }}>Explore the full range to find your match.</p>
        <Link href="/shop" className="mt-3 inline-block font-medium" style={{ color: tone.link }}>
          Explore the range →
        </Link>
        {/* Same degrade as above — rec.heroSlug didn't match any set, so
            there's nothing to pin. */}
        {buildHref ? <BuildLinkPrompt href={buildHref} tone={tone} /> : null}
      </section>
    )
  }

  const onClick = (slug: string) => () => track(tenantSlug, 'quiz_click', slug, { session })

  // Pinned to the hero the customer was just told is "your match" — a bare
  // buildHref would open the configurator on /build's own default
  // (sort_order-first) model, which silently disagrees with the
  // recommendation above it. Only pin when the hero is actually in /build's
  // catalog (isBundle) — resolveProductFinderSets can recommend a
  // category='efoil_set' row that isn't a bundle, and pinning that slug would
  // make /build fall through to ITS OWN sort_order-first default (an
  // unmatched `set` behaves as if none was passed), silently landing on a
  // different, wrong board. Degrading to the un-pinned link is correct there,
  // same as the two fallback screens above (review round 2, F1).
  const buildLink = buildHref ? (hero.isBundle ? withRecommendedSet(buildHref, hero.slug) : buildHref) : null

  if (wide) {
    // Standalone /quiz payoff. The reveal the quiz promised: the board large,
    // the reason, the price, ONE primary action — then the alternative paths
    // as peer cards instead of a pile of 13px links.
    return (
      <section className="mx-auto w-full max-w-4xl px-[var(--section-padding-x,1rem)] py-10">
        <p className="text-xs uppercase tracking-wide" style={{ color: tone.muted }}>Your match</p>
        <div className="mt-4 grid items-center gap-6 md:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] md:gap-10">
          {hero.image ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={hero.image} alt={hero.name} className={`aspect-[4/3] w-full ${IMAGE_RADIUS} object-cover`} />
          ) : null}
          <div>
            <h3 className="text-2xl font-semibold md:text-3xl" style={{ ...HEADING_STYLE, color: tone.heading }}>
              {hero.name}
            </h3>
            <p className="mt-2 text-base" style={{ color: tone.muted }}>{rec.reason}</p>
            {hero.price != null ? (
              <p className="mt-3 text-xl font-semibold" style={{ color: tone.heading }}>${hero.price.toLocaleString('en-US')}</p>
            ) : null}
            <Link
              href={`/products/${hero.slug}${REC_QUERY}`}
              onClick={onClick(hero.slug)}
              className={`cta-primary mt-5 inline-block ${BUTTON_RADIUS} px-6 py-3 text-base font-medium transition-opacity hover:opacity-90`}
            >
              View this board →
            </Link>
          </div>
        </div>

        {runner || buildLink || shouldShowRentalBridge(answers, rentalsHref) ? (
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {runner ? (
              <Link
                href={`/products/${runner.slug}${REC_QUERY}`}
                onClick={onClick(runner.slug)}
                className={`flex items-center gap-4 ${BUTTON_RADIUS} border p-4 transition-colors hover:border-[var(--color-primary)] ${tone.divider} bg-[var(--color-surface-muted)]`}
              >
                {runner.image ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={runner.image} alt={runner.name} className={`h-16 w-16 shrink-0 ${IMAGE_RADIUS} object-cover`} />
                ) : null}
                <span className="min-w-0">
                  <span className="block text-xs uppercase tracking-wide" style={{ color: tone.muted }}>Also consider</span>
                  <span className="mt-0.5 block text-sm font-medium" style={{ color: tone.heading }}>{runner.name}</span>
                  {runner.price != null ? (
                    <span className="mt-0.5 block text-sm" style={{ color: tone.muted }}>${runner.price.toLocaleString('en-US')}</span>
                  ) : null}
                </span>
              </Link>
            ) : null}
            {buildLink ? (
              <Link
                href={buildLink}
                className={`flex flex-col justify-center ${BUTTON_RADIUS} border p-4 transition-colors hover:border-[var(--color-primary)] ${tone.divider} bg-[var(--color-surface-muted)]`}
              >
                <span className="text-xs uppercase tracking-wide" style={{ color: tone.muted }}>Prefer to pick every part?</span>
                <span className="mt-0.5 text-sm font-medium" style={{ color: tone.link }}>Build a Board →</span>
              </Link>
            ) : null}
            {shouldShowRentalBridge(answers, rentalsHref) ? (
              <Link
                href={rentalsHref}
                className={`flex flex-col justify-center ${BUTTON_RADIUS} border border-[var(--color-cta-edge)] bg-[var(--color-cta-soft)] p-4`}
              >
                <span className="text-sm font-medium text-[var(--color-text-primary)]">New to foiling? Try before you buy.</span>
                <span className="mt-0.5 text-[13px] text-[var(--color-text-secondary)]">
                  Book a session on the water first — then come back for the board that fits.
                </span>
                <span className="mt-1 text-sm font-medium text-[var(--color-cta-readable)]">Book a session →</span>
              </Link>
            ) : null}
          </div>
        ) : null}

        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
          {lite ? (
            <p className="text-[13px]" style={{ color: tone.muted }}>
              Need it to pack down for a boat or tight storage?{' '}
              <Link href={`/products/${lite.slug}`} style={{ color: tone.link }}>
                Ask about the Lite range →
              </Link>
            </p>
          ) : null}
          <button type="button" onClick={onRestart} className="text-[13px]" style={{ color: tone.link }}>
            Start over
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="mx-auto w-full max-w-[var(--section-max-width,480px)] px-[var(--section-padding-x,1rem)] py-10">
      <p className="text-xs uppercase tracking-wide" style={{ color: tone.muted }}>Your match</p>
      <div className="mt-2 flex items-center gap-4">
        {/* External media-CDN URL — next/image needs configured remotePatterns, so plain img (matches other client embed components). */}
        {hero.image ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={hero.image} alt={hero.name} className={`h-[90px] w-[120px] ${IMAGE_RADIUS} object-cover`} />
        ) : null}
        <div>
          <h3 className="text-base font-semibold" style={{ ...HEADING_STYLE, color: tone.heading }}>
            {hero.name}
          </h3>
          <p className="mt-1 text-sm" style={{ color: tone.muted }}>{rec.reason}</p>
          {hero.price != null ? (
            <p className="mt-1 text-sm font-medium" style={{ color: tone.heading }}>${hero.price.toLocaleString('en-US')}</p>
          ) : null}
          <Link
            href={`/products/${hero.slug}${REC_QUERY}`}
            onClick={onClick(hero.slug)}
            className={`cta-primary mt-2 inline-block ${BUTTON_RADIUS} px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90`}
          >
            View this board →
          </Link>
        </div>
      </div>

      {shouldShowRentalBridge(answers, rentalsHref) ? (
        // Deliberately NOT toned by onDark: the panel paints its own opaque
        // light background, so its dark text stays legible on any band.
        <div className="mt-4 rounded-[var(--layout-button-radius,var(--brand-radius,0.5rem))] border border-[var(--color-cta-edge)] bg-[var(--color-cta-soft)] px-4 py-3">
          <p className="text-sm font-medium text-[var(--color-text-primary)]">
            New to foiling? Try before you buy.
          </p>
          <p className="mt-0.5 text-[13px] text-[var(--color-text-secondary)]">
            Book a session on the water first — then come back for the board that fits.
          </p>
          <Link href={rentalsHref} className="mt-1 inline-block text-sm font-medium text-[var(--color-cta-readable)] hover:opacity-80">
            Book a session →
          </Link>
        </div>
      ) : null}

      {runner ? (
        <div className={`mt-4 flex items-center gap-3 border-t pt-4 ${tone.divider}`}>
          {runner.image ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={runner.image} alt={runner.name} className={`h-14 w-14 ${IMAGE_RADIUS} object-cover`} />
          ) : null}
          <div>
            <p className="text-xs uppercase tracking-wide" style={{ color: tone.muted }}>Also consider</p>
            <Link
              href={`/products/${runner.slug}${REC_QUERY}`}
              onClick={onClick(runner.slug)}
              className="text-sm font-medium"
              style={{ color: tone.link }}
            >
              {runner.name}
              {runner.price != null ? ` — $${runner.price.toLocaleString('en-US')}` : ''}
            </Link>
          </div>
        </div>
      ) : null}

      {lite ? (
        <p className="mt-4 text-[13px]" style={{ color: tone.muted }}>
          Need it to pack down for a boat or tight storage?{' '}
          <Link href={`/products/${lite.slug}`} style={{ color: tone.link }}>
            Ask about the Lite range →
          </Link>
        </p>
      ) : null}

      {buildLink ? <BuildLinkPrompt href={buildLink} tone={tone} /> : null}

      <button type="button" onClick={onRestart} className="mt-4 text-[13px]" style={{ color: tone.link }}>
        Start over
      </button>
    </section>
  )
}
